import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/use-toast';
import usePageTitle from '../hooks/usePageTitle';
import {
  adminLogin, adminVerify, getInquiries, deleteInquiry, getQuizResults, deleteQuizResult, setQuizBooked,
  getAllTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  getAllLounge, createLounge, updateLounge, deleteLounge,
} from '../api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ConfirmDelete } from '../components/ConfirmDelete';
import { downloadCsv } from '../lib/csv';
import { Lock, LogOut, Trash2, Plus, Pencil, X, Mail, Phone, Star, Download } from 'lucide-react';

const inputCls = 'bg-white border border-[#D6CEE0] rounded-sm px-3 h-11 text-[#241033] focus-visible:ring-1 focus-visible:ring-[#43265E] font-light';

const Admin = () => {
  usePageTitle('Admin Dashboard');
  const { toast } = useToast();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState('inquiries');

  useEffect(() => {
    if (sessionStorage.getItem('tag_admin_token')) {
      adminVerify().then(() => setAuthed(true)).catch(() => { sessionStorage.removeItem('tag_admin_token'); });
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { token } = await adminLogin(password);
      sessionStorage.setItem('tag_admin_token', token);
      setAuthed(true);
      toast({ title: 'Welcome back, Janiece' });
    } catch (err) {
      toast({ title: 'Incorrect password' });
    }
  };

  const logout = () => { sessionStorage.removeItem('tag_admin_token'); setAuthed(false); setPassword(''); };

  if (!authed) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#241033] px-6">
        <form onSubmit={login} className="bg-[#FBFAF8] rounded-sm p-10 w-full max-w-md shadow-2xl">
          <div className="w-12 h-12 rounded-full bg-[#43265E] flex items-center justify-center mb-6"><Lock className="w-5 h-5 text-white" strokeWidth={1.5} /></div>
          <h1 className="font-display text-3xl text-[#241033] mb-2">Admin Access</h1>
          <p className="text-[#6A6570] font-light mb-8 text-sm">The Answer Group Consulting — private dashboard.</p>
          <label className="eyebrow text-[#7E5C9E] block mb-2">Password</label>
          <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + ' w-full mb-6'} placeholder="Enter password" />
          <button data-testid="admin-login-btn" type="submit" className="btn-plum rounded-full px-8 py-3 text-sm tracking-luxe uppercase w-full">Sign In</button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F4F1EC] pt-28 pb-24">
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="eyebrow text-[#7E5C9E] mb-1">TAG Consulting</div>
            <h1 className="font-display text-4xl text-[#241033]">Admin Dashboard</h1>
          </div>
          <button onClick={logout} className="btn-outline rounded-full px-6 py-2.5 text-sm tracking-luxe uppercase inline-flex items-center gap-2"><LogOut className="w-4 h-4" strokeWidth={1.5} /> Log Out</button>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap">
          {[['inquiries', 'Inquiries'], ['quiz', 'Quiz Results'], ['newsletter', 'Newsletter'], ['testimonials', 'Testimonials'], ['lounge', 'The Lounge']].map(([id, label]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`px-6 py-2.5 rounded-full text-sm tracking-luxe uppercase transition-all ${tab === id ? 'bg-[#43265E] text-white' : 'bg-white text-[#4A4453] hover:bg-[#EDE8F2]'}`}>{label}</button>
          ))}
        </div>

        {tab === 'inquiries' && <Inquiries toast={toast} />}
        {tab === 'quiz' && <QuizResults toast={toast} />}
        {tab === 'newsletter' && <Newsletter />}
        {tab === 'testimonials' && <TestimonialsAdmin toast={toast} />}
        {tab === 'lounge' && <LoungeAdmin toast={toast} />}
      </div>
    </main>
  );
};

const Inquiries = ({ toast }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => getInquiries().then(setItems).catch(() => {}), [setItems]);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { await deleteInquiry(id); toast({ title: 'Inquiry deleted' }); load(); };

  if (!items.length) return <div className="bg-white rounded-sm p-10 text-center text-[#6A6570] font-light">No inquiries yet. Submitted contact forms will appear here.</div>;
  return (
    <div className="space-y-4">
      {items.map((it) => (
        <div key={it.id} className="bg-white rounded-sm p-6 flex items-start justify-between gap-6">
          <div>
            <div className="font-display text-xl text-[#241033]">{it.firstName} {it.lastName}{it.subscribe && <span className="ml-3 text-xs bg-[#EDE8F2] text-[#43265E] px-2 py-0.5 rounded-full tracking-luxe uppercase">Subscribed</span>}</div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#6A6570] font-light mt-1 mb-3">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {it.email}</span>
              {it.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {it.phone}</span>}
            </div>
            <p className="text-[#3A3444] font-light">{it.message}</p>
            <div className="text-xs text-[#9A8AAF] mt-3">{new Date(it.createdAt).toLocaleString()}</div>
          </div>
          <ConfirmDelete testId={`inquiry-delete-${it.id}`} label={`the inquiry from ${it.firstName} ${it.lastName}`} onConfirm={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

const DownloadBar = ({ count, noun, onDownload, testId }) => (
  <div className="flex items-center justify-between bg-white rounded-sm px-6 py-4">
    <span className="text-sm text-[#6A6570] font-light">{count} {noun}{count === 1 ? '' : 's'}</span>
    <button data-testid={testId} onClick={onDownload} className="btn-outline rounded-full px-5 py-2 text-xs tracking-luxe uppercase inline-flex items-center gap-2"><Download className="w-4 h-4" strokeWidth={1.5} /> Download CSV</button>
  </div>
);

const Newsletter = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { getInquiries().then((all) => setItems(all.filter((i) => i.subscribe))).catch(() => {}); }, []);
  const download = () => downloadCsv('tag-newsletter-signups.csv', ['First Name', 'Last Name', 'Email', 'Phone', 'Signed Up'],
    items.map((i) => [i.firstName, i.lastName, i.email, i.phone, new Date(i.createdAt).toLocaleString()]));
  if (!items.length) return <div data-testid="newsletter-empty" className="bg-white rounded-sm p-10 text-center text-[#6A6570] font-light">No newsletter signups yet. Contact form visitors who tick “subscribe” will appear here.</div>;
  return (
    <div className="space-y-4" data-testid="newsletter-list">
      <DownloadBar count={items.length} noun="subscriber" onDownload={download} testId="newsletter-download-btn" />
      <div className="bg-white rounded-sm divide-y divide-[#EDE8F2]">
        {items.map((i) => (
          <div key={i.id} data-testid={`newsletter-row-${i.id}`} className="px-6 py-4 flex flex-wrap justify-between gap-2">
            <span className="font-display text-lg text-[#241033]">{i.firstName} {i.lastName}</span>
            <span className="text-sm text-[#6A6570] font-light inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {i.email}</span>
            <span className="text-xs text-[#9A8AAF]">{new Date(i.createdAt).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const QuizResults = ({ toast }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => getQuizResults().then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { await deleteQuizResult(id); toast({ title: 'Quiz result deleted' }); load(); };
  const toggleBooked = async (id, booked) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, booked } : it)));
    await setQuizBooked(id, booked).catch(() => { toast({ title: 'Could not update' }); load(); });
  };

  if (!items.length) return <div data-testid="quiz-results-empty" className="bg-white rounded-sm p-10 text-center text-[#6A6570] font-light">No quiz results yet. Completed readiness quizzes will appear here.</div>;
  const download = () => {
    const questions = [...new Set(items.flatMap((it) => it.answers.map((a) => a.question)))];
    downloadCsv('tag-quiz-results.csv',
      ['Date', 'Name', 'Email', 'Organization', 'Score (%)', 'Level', 'Booked', 'Reminder Sent', ...questions],
      items.map((it) => [new Date(it.createdAt).toLocaleString(), it.name, it.email, it.organization, it.score, it.level, it.booked ? 'Yes' : 'No', it.reminderSentAt ? new Date(it.reminderSentAt).toLocaleString() : '',
        ...questions.map((q) => it.answers.find((a) => a.question === q)?.answer ?? '')]));
  };
  return (
    <div className="space-y-4" data-testid="quiz-results-list">
      <DownloadBar count={items.length} noun="quiz result" onDownload={download} testId="quiz-results-download-btn" />
      {items.map((it) => (
        <details key={it.id} data-testid={`quiz-result-${it.id}`} className="bg-white rounded-sm p-6 group">
          <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
            <div>
              <div className="font-display text-xl text-[#241033]">{it.name}<span className="ml-3 inline-block whitespace-nowrap text-xs bg-[#EDE8F2] text-[#43265E] px-2 py-0.5 rounded-full tracking-luxe uppercase">{it.score}% · {it.level}</span></div>
              <div className="flex flex-wrap gap-x-5 text-sm text-[#6A6570] font-light mt-1">
                <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {it.email}</span>
                {it.organization && <span>{it.organization}</span>}
                <span className="text-[#9A8AAF]">{new Date(it.createdAt).toLocaleString()}</span>
                {it.reminderSentAt && <span data-testid={`quiz-reminder-sent-${it.id}`} className="text-[#9A8AAF]">Reminder sent {new Date(it.reminderSentAt).toLocaleDateString()}</span>}
              </div>
            </div>
            <span onClick={(e) => e.preventDefault()} className="flex items-center gap-5 shrink-0">
              <label className="flex items-center gap-2 text-xs tracking-luxe uppercase text-[#7E5C9E] cursor-pointer">
                <Switch data-testid={`quiz-booked-toggle-${it.id}`} checked={!!it.booked} onCheckedChange={(v) => toggleBooked(it.id, v)} /> Booked
              </label>
              <ConfirmDelete testId={`quiz-result-delete-${it.id}`} label={`the quiz result for ${it.name}`} onConfirm={() => remove(it.id)} /></span>
          </summary>
          <ul className="mt-4 space-y-2 border-t border-[#EDE8F2] pt-4">
            {it.answers.map((a) => <li key={a.question} className="text-sm font-light text-[#3A3444]"><span className="text-[#7E5C9E]">{a.question}</span><br />{a.answer}</li>)}
          </ul>
        </details>
      ))}
    </div>
  );
};

const emptyTest = { quote: '', name: '', context: '', rating: 5, published: true, order: 0 };
const TestimonialsAdmin = ({ toast }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => getAllTestimonials().then(setItems).catch(() => {}), [setItems]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.quote || !editing.name) { toast({ title: 'Quote and name are required' }); return; }
    try {
      if (editing.id) await updateTestimonial(editing.id, editing);
      else await createTestimonial(editing);
      toast({ title: 'Saved' }); setEditing(null); load();
    } catch { toast({ title: 'Save failed' }); }
  };
  const remove = async (id) => { await deleteTestimonial(id); toast({ title: 'Deleted' }); load(); };

  return (
    <div>
      <button onClick={() => setEditing({ ...emptyTest, order: items.length })} className="btn-plum rounded-full px-6 py-2.5 text-sm tracking-luxe uppercase inline-flex items-center gap-2 mb-6"><Plus className="w-4 h-4" strokeWidth={1.5} /> New Testimonial</button>
      <div className="space-y-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white rounded-sm p-6 flex items-start justify-between gap-6">
            <div>
              <div className="flex gap-1 mb-2">{Array.from({ length: t.rating }).map((_, i) => <Star key={`${t.id}-star-${i}`} className="w-3.5 h-3.5 text-[#43265E] fill-[#43265E]" strokeWidth={1} />)}</div>
              <p className="font-display text-lg text-[#241033] italic">“{t.quote}”</p>
              <div className="text-sm text-[#7E5C9E] tracking-luxe uppercase mt-2">{t.name} · {t.context} {!t.published && <span className="text-[#B9A9CC] normal-case">(hidden)</span>}</div>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => setEditing(t)} className="text-[#9A8AAF] hover:text-[#43265E]"><Pencil className="w-5 h-5" strokeWidth={1.5} /></button>
              <button onClick={() => remove(t.id)} className="text-[#9A8AAF] hover:text-[#43265E]"><Trash2 className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-[#FBFAF8] rounded-sm p-8 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6"><h3 className="font-display text-2xl text-[#241033]">{editing.id ? 'Edit' : 'New'} Testimonial</h3><button onClick={() => setEditing(null)}><X className="w-5 h-5 text-[#6A6570]" /></button></div>
            <div className="space-y-4">
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Quote</label><Textarea rows={4} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} className="bg-white border border-[#D6CEE0] rounded-sm text-[#241033] font-light" /></div>
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Name / Title</label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls + ' w-full'} /></div>
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Context (service)</label><Input value={editing.context} onChange={(e) => setEditing({ ...editing, context: e.target.value })} className={inputCls + ' w-full'} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="eyebrow text-[#7E5C9E] block mb-2">Rating (1-5)</label><Input type="number" min={1} max={5} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: parseInt(e.target.value) || 5 })} className={inputCls + ' w-full'} /></div>
                <div><label className="eyebrow text-[#7E5C9E] block mb-2">Order</label><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} className={inputCls + ' w-full'} /></div>
              </div>
              <label className="flex items-center gap-3"><Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} /> <span className="text-sm font-light text-[#4A4453]">Published (visible on site)</span></label>
              <button onClick={save} className="btn-plum rounded-full px-8 py-3 text-sm tracking-luxe uppercase w-full mt-2">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const emptyPost = { title: '', category: 'Reflection', excerpt: '', content: '', image: '', readTime: '4 min read', date: '', published: true };
const LoungeAdmin = ({ toast }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => getAllLounge().then(setItems).catch(() => {}), [setItems]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title) { toast({ title: 'Title is required' }); return; }
    try {
      if (editing.id) await updateLounge(editing.id, editing);
      else await createLounge(editing);
      toast({ title: 'Saved' }); setEditing(null); load();
    } catch { toast({ title: 'Save failed' }); }
  };
  const remove = async (id) => { await deleteLounge(id); toast({ title: 'Deleted' }); load(); };

  return (
    <div>
      <button onClick={() => setEditing({ ...emptyPost })} className="btn-plum rounded-full px-6 py-2.5 text-sm tracking-luxe uppercase inline-flex items-center gap-2 mb-6"><Plus className="w-4 h-4" strokeWidth={1.5} /> New Article</button>
      <div className="space-y-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-sm p-6 flex items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              {p.image && <img src={p.image} alt="" className="w-16 h-16 object-cover rounded-sm" />}
              <div>
                <div className="font-display text-lg text-[#241033]">{p.title} {!p.published && <span className="text-xs text-[#B9A9CC]">(draft)</span>}</div>
                <div className="text-sm text-[#7E5C9E] tracking-luxe uppercase mt-1">{p.category} · {p.date}</div>
              </div>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => setEditing(p)} className="text-[#9A8AAF] hover:text-[#43265E]"><Pencil className="w-5 h-5" strokeWidth={1.5} /></button>
              <button onClick={() => remove(p.id)} className="text-[#9A8AAF] hover:text-[#43265E]"><Trash2 className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-[#FBFAF8] rounded-sm p-8 w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6"><h3 className="font-display text-2xl text-[#241033]">{editing.id ? 'Edit' : 'New'} Article</h3><button onClick={() => setEditing(null)}><X className="w-5 h-5 text-[#6A6570]" /></button></div>
            <div className="space-y-4">
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Title</label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls + ' w-full'} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="eyebrow text-[#7E5C9E] block mb-2">Category</label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls + ' w-full'} /></div>
                <div><label className="eyebrow text-[#7E5C9E] block mb-2">Read time</label><Input value={editing.readTime} onChange={(e) => setEditing({ ...editing, readTime: e.target.value })} className={inputCls + ' w-full'} /></div>
                <div><label className="eyebrow text-[#7E5C9E] block mb-2">Date</label><Input value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} placeholder="June 2026" className={inputCls + ' w-full'} /></div>
              </div>
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Image URL</label><Input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} className={inputCls + ' w-full'} /></div>
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Excerpt</label><Textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="bg-white border border-[#D6CEE0] rounded-sm text-[#241033] font-light" /></div>
              <div><label className="eyebrow text-[#7E5C9E] block mb-2">Content (new line = new paragraph)</label><Textarea rows={8} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="bg-white border border-[#D6CEE0] rounded-sm text-[#241033] font-light" /></div>
              <label className="flex items-center gap-3"><Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} /> <span className="text-sm font-light text-[#4A4453]">Published (visible on site)</span></label>
              <button onClick={save} className="btn-plum rounded-full px-8 py-3 text-sm tracking-luxe uppercase w-full mt-2">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
